import React, { useMemo, useState, useEffect, memo } from 'react';
import {
  getStatus,
  getStatusInfo,
  isAlarmStatus,
  hasAttention,
} from '../../../../utils';
import { getThingActivity } from '../../../../services';
import { getMarkerFillColor } from './marker-helpers';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  AlertTriangleIcon,
  getSeverityKey,
  formatTimeAgo,
  formatNumber,
  formatDuration,
  isOldAlarm,
  buildAlarmAriaLabel,
} from './alarm-helpers';
import * as s from './styled-pracas-mapa';

// Helpers no escopo do componente — operam no struct getStatusInfo
// em vez de fazer .includes() em string concatenada (frágil a typo /
// acento / ordem). Vide utils/index.js getStatusInfo.
const markerStatusInfo = (m) =>
  getStatusInfo(m.statusMap.status, m.statusMap.isRangeWarning);

// Renderiza uma lista de marker como AlertItems. Extraido em funcao
// pura pra reutilizar entre os dois grupos (alarme / atencao) com
// divisor visual entre eles.
const renderGroup = (group, ctx) => {
  const {
    onAlertClick,
    freshAlarms,
    $cco,
    onAckAlert,
    onHoverAlert,
    hoveredId,
    onAskAi,
  } = ctx;
  return group.map((marker) => {
    const statusInfo = markerStatusInfo(marker);
    // getStatus legacy ainda usado pra: (1) markerStatus label no aria,
    // (2) getMarkerFillColor (que faz string-match). Migracao desses
    // pode vir em iteracoes seguintes — aqui mantemos compat.
    const markerStatus = getStatus(
      marker.statusMap.status,
      marker.statusMap.isRangeWarning,
    );
    const errorCount =
      marker.statusMap && marker.statusMap.status
        ? marker.statusMap.status.E
        : 0;
    const dotColor = getMarkerFillColor(markerStatus);
    const isFresh = !!freshAlarms[marker.id];

    const { topAlarm, statusSince } = marker;
    const severity = topAlarm ? getSeverityKey(topAlarm.level) : null;
    const tAgo = topAlarm ? formatTimeAgo(topAlarm.since) : null;
    // Park esta em ALARME (vs atencao) — usa struct, nao string-match.
    const isAlarmingPark = isAlarmStatus(statusInfo);
    const durationText =
      isAlarmingPark && statusSince ? formatDuration(statusSince) : null;
    const isOld = isAlarmingPark && isOldAlarm(statusSince);
    const ariaLabel = topAlarm
      ? buildAlarmAriaLabel(topAlarm)
      : `${marker.name} — ${markerStatus}`;

    return (
      <s.AlertItem
        key={`alert-${marker.id}`}
        type="button"
        onClick={() => onAlertClick(marker)}
        onMouseEnter={() => onHoverAlert && onHoverAlert(marker.id)}
        onMouseLeave={() => onHoverAlert && onHoverAlert(null)}
        title={
          durationText
            ? `${ariaLabel} (há ${durationText} em alarme)`
            : ariaLabel
        }
        $fresh={isFresh}
        $old={isOld}
        $cco={$cco}
        $hovered={hoveredId === marker.id}
        $severity={isAlarmingPark ? 'alarm' : 'attention'}
      >
        {/* Tempo no canto sup. direito (mesma posicao das atividades).
            Em alarme vira pill SLA (tempo em alarme); senao, recencia. */}
        {$cco && (durationText || tAgo) && (
          <s.ItemTimeTag
            $sla={!!durationText}
            $old={isOld}
            title={durationText ? 'Tempo em alarme' : 'Recência do alarme'}
          >
            {durationText || tAgo}
          </s.ItemTimeTag>
        )}
        <s.AlertItemDot
          color={dotColor}
          attention={hasAttention(statusInfo)}
          $cco={$cco}
        />
        <s.AlertItemContent>
          <s.AlertItemName $cco={$cco}>
            <span className="nm">{marker.name}</span>
            {durationText && !$cco && (
              <s.AlertItemDuration $old={isOld} title="Tempo em alarme">
                {durationText}
              </s.AlertItemDuration>
            )}
          </s.AlertItemName>
          {marker.customer && marker.customer.name && (
            <s.AlertItemCustomer $cco={$cco}>
              {marker.customer.name}
            </s.AlertItemCustomer>
          )}
          {topAlarm && (topAlarm.thingName || topAlarm.applianceName) && (
            <s.AlertItemMachine $cco={$cco} title="Maquina em alerta">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <path d="M3.27 6.96 12 12.01l8.73-5.05" />
                <path d="M12 22.08V12" />
              </svg>
              {topAlarm.thingName || topAlarm.applianceName}
            </s.AlertItemMachine>
          )}
          {topAlarm && (
            <s.AlertItemCause $cco={$cco} $severity={severity}>
              {topAlarm.direction === 'low' ? (
                <ArrowDownIcon size={12} />
              ) : topAlarm.direction === 'high' ? (
                <ArrowUpIcon size={12} />
              ) : (
                <AlertTriangleIcon size={12} />
              )}
              <span className="text">
                <span className="var">{topAlarm.variable}</span>
                {topAlarm.source !== 'firmware' && (
                  <>
                    <span className="val">
                      {formatNumber(topAlarm.value)}
                      {topAlarm.unit ? ` ${topAlarm.unit}` : ''}
                    </span>
                    <span className="lim">
                      ({topAlarm.direction === 'low' ? '<' : '>'}
                      {formatNumber(topAlarm.threshold)})
                    </span>
                  </>
                )}
                {tAgo && !$cco && (
                  <>
                    <span className="sep">·</span>
                    <span className="ago">{tAgo}</span>
                  </>
                )}
              </span>
            </s.AlertItemCause>
          )}
        </s.AlertItemContent>
        {errorCount > 0 && !$cco && (
          <s.AlertItemBadge>{errorCount}</s.AlertItemBadge>
        )}
        {$cco && (
          <>
            {/* Botao 'Ack' (✓) — esconde o alerta por 5min sem navegar.
                stopPropagation pra nao disparar o onClick do AlertItem
                que abriria o detail panel. Visivel so no hover do item
                (CSS opacity). */}
            {/* Triagem IA (✨): abre o copiloto com a pergunta de contexto
                do alarme pre-montada. So aparece quando o LLM esta
                habilitado (onAskAi vem undefined sem ANTHROPIC_API_KEY). */}
            {onAskAi && marker.topAlarm && (
              <s.AlertItemAck
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAskAi(marker);
                }}
                title="Triagem com IA — recorrência e o que verificar"
                aria-label="Triagem do alarme com IA"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z" />
                </svg>
              </s.AlertItemAck>
            )}
            {onAckAlert && (
              <s.AlertItemAck
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAckAlert(marker.id);
                }}
                title="Marcar como visto (esconde por 5 min)"
                aria-label="Marcar alerta como visto"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </s.AlertItemAck>
            )}
            <span className="chev" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </span>
          </>
        )}
      </s.AlertItem>
    );
  });
};

// Overlay flutuante no canto superior direito do mapa listando parques
// em alarme/bloqueio/atencao para triagem rapida. Componente extraido
// do pracas-mapa.js para reduzir tamanho.
//
// markers: array de markers do mapa
// onAlertClick(marker): chamado quando o operador clica num item
// freshAlarms: map { [parqueId]: timestamp } pra marcar visualmente os
//   alarmes recem-detectados (<30s). Quando setado, o item da lista
//   ganha um pulse vermelho extra pra reforcar atencao.
// Tempo relativo curto pra notificacoes de atividade (ts em epoch ms).
// "agora" / "há Nmin". TTL e curto (60s), entao nao precisa de horas/dias.
const formatActivityAgo = (ts) => {
  const diff = Date.now() - ts;
  if (diff < 15000) return 'agora';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'há instantes';
  return `há ${min} min`;
};

// No historico os eventos podem ser antigos — mostramos data+hora absoluta
// ('dd/MM HH:mm') em vez de relativo. Manual (sem date-fns) pra evitar
// dependencia extra neste componente.
const formatHistoryTime = (ts) => {
  const d = new Date(ts);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}`;
};

const AlertOverlay = ({
  markers,
  onAlertClick,
  freshAlarms = {},
  ackedAlerts = {},
  onAckAlert,
  // Notificacoes transitorias de transicao por-maquina (comecou a rodar,
  // desligou, ficou sem dados, recuperou). Vem do useActivityNotifications.
  // Auto-expiram — aqui so renderizamos.
  activity = [],
  // Historico persistente das notificacoes (nao expira). Abre via rodape.
  history = [],
  onClearHistory,
  // Navega pro centro de notificacoes (/notifications) — integracao do
  // painel ao-vivo com o registro persistente de eventos.
  onViewAllNotifications,
  // Realce bidirecional lista<->mapa: hover num item destaca o marker no
  // mapa (e vice-versa). onHoverAlert(parqueId|null); hoveredId = atual.
  onHoverAlert,
  hoveredId,
  // Triagem IA do alarme (abre o copiloto com pergunta pre-montada).
  // undefined quando o LLM nao esta habilitado no backend.
  onAskAi,
  $cco = false,
  // Fundo transparente pra deixar o liquid glass do dock (telao) aparecer.
  glass = false,
}) => {
  // Alterna o corpo do painel entre "ao vivo" e "histórico" (UI local).
  const [showHistory, setShowHistory] = useState(false);
  // Historico server-side (duravel/cross-operador). Buscado sob demanda ao
  // abrir a aba. null = ainda nao buscado; [] = buscado e vazio. Em erro
  // (ex.: endpoint nao deployado), cai no fallback do localStorage (prop
  // `history`). Normaliza o item do backend pro mesmo shape do feed.
  const [serverHistory, setServerHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  useEffect(() => {
    if (!showHistory) return undefined;
    let mounted = true;
    setHistoryLoading(true);
    setHistoryError(false);
    getThingActivity({ limit: 50 })
      .then((resp) => {
        if (!mounted) return;
        const items = (resp && resp.data && resp.data.items) || [];
        setServerHistory(
          items.map((it) => ({
            key: `srv-${it.id}`,
            thingName: it.thingDisplayName || it.thingName,
            // No log mostramos o status alcancado (toStatus) como rotulo.
            label: (it.toStatus && it.toStatus.name) || '—',
            color: (it.toStatus && it.toStatus.color) || 'var(--cco-ink-faint)',
            parkName: it.parkName,
            ts: it.ts ? new Date(it.ts).getTime() : Date.now(),
          })),
        );
      })
      .catch(() => {
        // Fallback pro localStorage (prop history) quando o endpoint falha.
        if (mounted) setHistoryError(true);
      })
      .finally(() => {
        if (mounted) setHistoryLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [showHistory]);
  // Sort melhorado em 4 niveis:
  //   1. Severidade (alarme > atencao)
  //   2. Fresh alarms primeiro (alerta detectado nos ultimos 30s)
  //   3. Recencia (topAlarm.since DESC — alarme mais novo no topo)
  //   4. Alfabetico (cliente + nome) pra determinismo final
  //
  // Visual: divisor 'ATENCAO' separa as duas camadas pra evidenciar
  // mudanca de severidade, evita operador confundir o boundary.
  const { alarmGroup, attentionGroup, alarmCount, attentionCount } = useMemo(() => {
    if (!markers || !markers.length) {
      return { alarmGroup: [], attentionGroup: [], alarmCount: 0, attentionCount: 0 };
    }
    // Classifica via struct, nao string-match. isAlarm + isAttention
    // ortogonais — um park pode ter ambos (alarme + atencao, ex.:
    // ALARME firmware bit 6 + range WH ativo no mesmo tick). No grupo
    // de alarme tem prioridade.
    const isAlarm = (m) => isAlarmStatus(markerStatusInfo(m));
    const hasAttn = (m) => hasAttention(markerStatusInfo(m));

    // Filtra fora os parks que operador 'ackou' nos ultimos 5 min — eles
    // continuam vermelhos no mapa (logica real intacta), so somem da lista
    // pra reduzir ruido enquanto o operador resolve outros itens.
    const list = markers.filter((m) => {
      if (ackedAlerts && ackedAlerts[m.id]) return false;
      return isAlarm(m) || hasAttn(m);
    });

    const cmp = (a, b) => {
      // 1. SLA: alarmes antigos (>15min em alarme) primeiro — operador
      // precisa ver o que ja persiste sem resolucao. Tem prioridade ate
      // sobre 'fresh' porque alarme antigo nunca-resolvido e pior cenario
      // operacional que alarme novo recem-detectado.
      const aOld = isOldAlarm(a.statusSince) ? 1 : 0;
      const bOld = isOldAlarm(b.statusSince) ? 1 : 0;
      if (aOld !== bOld) return bOld - aOld;
      // 2. Fresh primeiro dentro da mesma severidade
      const aFresh = freshAlarms[a.id] ? 1 : 0;
      const bFresh = freshAlarms[b.id] ? 1 : 0;
      if (aFresh !== bFresh) return bFresh - aFresh;
      // 3. statusSince (duracao em alarme) — mais antigo primeiro dentro
      // dos 'old', mais novo primeiro dentro dos 'fresh'/normais.
      const aStatusSince = a.statusSince || 0;
      const bStatusSince = b.statusSince || 0;
      if (aStatusSince !== bStatusSince) {
        // Entre 'old', menor since (mais antigo em alarme) primeiro
        if (aOld && bOld) return aStatusSince - bStatusSince;
        // Entre os demais, maior since (mais recente) primeiro
        return bStatusSince - aStatusSince;
      }
      // 4. Recencia: topAlarm.since maior (mais recente) primeiro
      const aSince = (a.topAlarm && a.topAlarm.since) || 0;
      const bSince = (b.topAlarm && b.topAlarm.since) || 0;
      if (aSince !== bSince) return bSince - aSince;
      // 5. Alfa por cliente -> nome do parque
      const ca = (a.customer && a.customer.name) || '';
      const cb = (b.customer && b.customer.name) || '';
      const clientCmp = ca.localeCompare(cb, 'pt-BR');
      if (clientCmp !== 0) return clientCmp;
      return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    };

    // Divide em dois grupos pra renderizar com divisor visual entre eles.
    // Alarme inclui parks que tem ambos (alarme + atencao no mesmo park);
    // atencao pura (sem alarme primario) vai pro outro grupo.
    const alarms = list.filter(isAlarm).sort(cmp);
    const attentions = list.filter((m) => !isAlarm(m) && hasAttn(m)).sort(cmp);

    // Limite total de 8 — alarme tem prioridade. Se overflow, recorta
    // primeiro do atencao.
    const MAX = 8;
    const alarmsTrim = alarms.slice(0, MAX);
    const remaining = Math.max(0, MAX - alarmsTrim.length);
    const attentionsTrim = attentions.slice(0, remaining);

    return {
      alarmGroup: alarmsTrim,
      attentionGroup: attentionsTrim,
      // Contagem TOTAL (pre-trim) pra compor o header de forma fiel mesmo
      // quando a lista e recortada no MAX.
      alarmCount: alarms.length,
      attentionCount: attentions.length,
    };
  }, [markers, freshAlarms, ackedAlerts]);

  // Pior severidade presente: vermelho/critico se ha algum alarme; senao
  // laranja/atencao. Dirige a cor do dot e do label do header — assim uma
  // lista so de atencao nao "parece critica".
  const worstSeverity = alarmCount > 0 ? 'alarm' : 'attention';

  const alertMarkers = useMemo(
    () => [...alarmGroup, ...attentionGroup],
    [alarmGroup, attentionGroup],
  );

  // Painel aparece se ha alertas OU atividade recente. So-atividade
  // (nenhum critico/atencao) e um cenario valido: maquina comecou a rodar
  // num parque que esta todo OK.
  const hasAlerts = alertMarkers.length > 0;
  const hasActivity = activity.length > 0;
  const isEmpty = !hasAlerts && !hasActivity;

  // Vazio: no layout $cco o painel e uma coluna fixa do grid — em vez de
  // sumir (deixando a coluna preta), mostramos um estado vazio calmo
  // ("nada pra ver = bom sinal"). No overlay legado (absolute sobre o
  // mapa) mantemos o retorno null pra nao deixar um card flutuando atoa.
  if (isEmpty && !$cco) return null;

  // Em modo historico o painel deixa de ser "ao vivo" — header neutro.
  const inHistory = showHistory;
  // Fonte do historico: server-side quando disponivel; senao fallback pro
  // localStorage (prop `history`). usingServerHistory tambem decide se o
  // botao "Limpar" aparece (so faz sentido no buffer local — log e imutavel).
  const usingServerHistory = !historyError && serverHistory !== null;
  const historyItems = usingServerHistory ? serverHistory : history;
  // Painel deve aparecer tambem quando ha historico a oferecer (mesmo sem
  // alerta/atividade ativos no momento) no layout $cco.
  const hasHistoryAccess = $cco || history.length > 0;

  // Header adapta cor/label ao conteudo: historico (neutro), alerta
  // (vermelho/laranja), so atividade (azul), ou vazio (verde "tudo normal").
  const headerSeverity = inHistory
    ? 'activity'
    : hasAlerts
    ? worstSeverity
    : hasActivity
    ? 'activity'
    : 'ok';
  const headerLabel = inHistory
    ? 'Histórico'
    : hasAlerts
    ? 'Em alerta agora'
    : hasActivity
    ? 'Atividade'
    : 'Operação';

  return (
    <s.AlertOverlay $cco={$cco} $glass={glass}>
      <s.AlertHeader $cco={$cco} $severity={headerSeverity}>
        {$cco ? (
          <>
            <span className="lbl">
              <s.AlertHeaderDot $cco={$cco} $severity={headerSeverity} />
              {headerLabel}
            </span>
            <span className="ct">
              {inHistory
                ? historyLoading
                  ? 'carregando…'
                  : `${historyItems.length} ${
                      historyItems.length === 1 ? 'evento' : 'eventos'
                    }`
                : isEmpty
                ? 'Tudo normal'
                : [
                    alarmCount > 0
                      ? `${alarmCount} ${
                          alarmCount === 1 ? 'crítico' : 'críticos'
                        }`
                      : null,
                    attentionCount > 0 ? `${attentionCount} atenção` : null,
                    !hasAlerts && hasActivity
                      ? `${activity.length} ${
                          activity.length === 1 ? 'evento' : 'eventos'
                        }`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </span>
          </>
        ) : (
          <>
            <s.AlertHeaderDot $severity={headerSeverity} />
            {inHistory
              ? `Histórico (${historyItems.length})`
              : hasAlerts
              ? `Em alerta agora (${alertMarkers.length})`
              : `Atividade (${activity.length})`}
          </>
        )}
      </s.AlertHeader>
      {inHistory ? (
        historyLoading && !historyItems.length ? (
          <s.AlertEmpty>
            <span className="sub">Carregando histórico…</span>
          </s.AlertEmpty>
        ) : !historyItems.length ? (
          <s.AlertEmpty>
            <span className="title">Sem histórico</span>
            <span className="sub">
              Nenhuma mudança de estado registrada ainda.
            </span>
          </s.AlertEmpty>
        ) : (
          <s.AlertList $cco={$cco}>
            {historyItems.map((h) => (
              <s.ActivityItem
                key={h.key || `${h.id}-${h.ts}`}
                $cco={$cco}
                $color={h.color}
              >
                <s.ActivityDot $color={h.color} />
                <s.ActivityBody>
                  <span className="evt">
                    <span className="nm" title={h.thingName}>
                      {h.thingName}
                    </span>{' '}
                    {h.label}
                  </span>
                  {h.parkName && (
                    <span className="park" title={h.parkName}>
                      {h.parkName}
                    </span>
                  )}
                </s.ActivityBody>
                {$cco ? (
                  <s.ItemTimeTag>{formatHistoryTime(h.ts)}</s.ItemTimeTag>
                ) : (
                  <s.ActivityTime>{formatHistoryTime(h.ts)}</s.ActivityTime>
                )}
              </s.ActivityItem>
            ))}
          </s.AlertList>
        )
      ) : isEmpty ? (
        <s.AlertEmpty>
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="m9 11 3 3L22 4" />
          </svg>
          <span className="title">Nenhum alerta ou evento</span>
          <span className="sub">Todos os parques operando normalmente.</span>
        </s.AlertEmpty>
      ) : (
        <s.AlertList $cco={$cco}>
        {/* Lista uniforme e continua: SEM rotulos de secao. A ordem
            (alarme > atencao > atividade) e a COR da faixa lateral de cada
            item sao o que diferencia a severidade/tipo. */}
        {renderGroup(alarmGroup, {
          onAlertClick,
          freshAlarms,
          $cco,
          onAckAlert,
          onHoverAlert,
          hoveredId,
          onAskAi,
        })}
        {renderGroup(attentionGroup, {
          onAlertClick,
          freshAlarms,
          $cco,
          onAckAlert,
          onHoverAlert,
          hoveredId,
          onAskAi,
        })}

        {/* Zona ATIVIDADE — notificacoes transitorias de transicao por
            maquina (comecou a rodar / desligou / sem dados / recuperou).
            Sem rotulo de secao: lista continua, a cor da faixa diferencia. */}
        {activity.length > 0 && (
          <>
            {activity.map((a) => (
              <s.ActivityItem
                key={`act-${a.id}-${a.ts}`}
                $cco={$cco}
                $color={a.color}
              >
                <s.ActivityDot $color={a.color} />
                <s.ActivityBody>
                  <span className="evt">
                    <span className="nm" title={a.thingName}>
                      {a.thingName}
                    </span>{' '}
                    {a.label}
                  </span>
                  {a.parkName && (
                    <span className="park" title={a.parkName}>
                      {a.parkName}
                    </span>
                  )}
                </s.ActivityBody>
                {$cco ? (
                  <s.ItemTimeTag>{formatActivityAgo(a.ts)}</s.ItemTimeTag>
                ) : (
                  <s.ActivityTime>{formatActivityAgo(a.ts)}</s.ActivityTime>
                )}
              </s.ActivityItem>
            ))}
          </>
        )}
        </s.AlertList>
      )}

      {/* Rodape: acesso ao historico. Fica fora do scroll (flex-shrink:0).
          No CCO o historico server-side esta sempre disponivel. */}
      {hasHistoryAccess && (
        <s.AlertFooter $cco={$cco}>
          {inHistory ? (
            <>
              <button
                type="button"
                className="link"
                onClick={() => setShowHistory(false)}
              >
                ‹ Ao vivo
              </button>
              {/* Limpar so no fallback local — o log server-side e imutavel. */}
              {!usingServerHistory && onClearHistory && (
                <button
                  type="button"
                  className="clear"
                  onClick={onClearHistory}
                >
                  Limpar
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="link"
                onClick={() => setShowHistory(true)}
              >
                Ver histórico
              </button>
              {onViewAllNotifications && (
                <button
                  type="button"
                  className="link"
                  onClick={onViewAllNotifications}
                >
                  Ver todas as notificações ›
                </button>
              )}
            </>
          )}
        </s.AlertFooter>
      )}
    </s.AlertOverlay>
  );
};

// memo: o PracasMapa re-renderiza a cada tick de polling (updatedAgoInfo,
// lastUpdate, etc). Sem memo, o AlertOverlay re-renderizava + re-sortava
// a lista inteira a cada ciclo mesmo quando markers/freshAlarms/acked
// nao mudaram — jank visivel no modo TV (60s). Props sao estaveis
// (callbacks via useCallback, objetos de estado memoizados no parent),
// entao a comparacao shallow do memo evita o trabalho.
export default memo(AlertOverlay);
